import { InputLabel, MenuItem, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReactElement } from "react";
import {
  Controller,
  FieldValues,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";

import { BasicFormInput } from "@stw/yup-validators";

import { RhfSelect } from "~frontend/components/forms/react-hook-form-components/index";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";

import { FlexBox } from "../../FlexBox";

export function timeToHHMM(hhmmss: string): string {
  return hhmmss.substring(0, 5);
}

export function timeFromHHMM(hhmm: string): string {
  if (hhmm) {
    return `${hhmm}:00`;
  }

  return "";
}

const inlineFormControlStyle = {
  paddingTop: 0,
  minWidth: "140px",
  flex: "1 0 0",
};

export function RhfTextingHoursFields({
  startTimeFieldName = "textingHoursStart",
  endTimeFieldName = "textingHoursEnd",
  timezoneFieldName = "textingHoursTimezone",
  disabled = false,
  label = null,
}: {
  startTimeFieldName?: string;
  endTimeFieldName?: string;
  timezoneFieldName?: string;
  disabled?: boolean;
  label?: string | null;
}): ReactElement {
  const { control } = useFormContext<BasicFormInput>();

  const theme = useTheme();
  const tenant = useTenant()!;

  return (
    <div>
      {label && (
        <InputLabel shrink sx={{ mb: -1.25 }}>
          {label}
        </InputLabel>
      )}
      <FlexBox alignItems="baseline" data-testid="texting-hours">
        <RhfTimeField
          data-testid="texting-hours-start"
          name={startTimeFieldName}
          disabled={disabled}
        />
        <span
          style={{
            display: "inline-block",
            padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
          }}
        >
          to
        </span>
        <RhfTimeField
          data-testid="texting-hours-end"
          name={endTimeFieldName}
          disabled={disabled}
        />
        <RhfSelect
          sx={{
            marginLeft: theme.spacing(3),
            width: "270px",
            ...inlineFormControlStyle,
          }}
          name={timezoneFieldName as any}
          control={control}
          disabled={disabled}
          data-testid="texting-hours-timezone"
        >
          {tenant.timezoneOptions.map(({ displayName, timezone }) => (
            <MenuItem key={timezone} value={timezone}>
              {displayName}
            </MenuItem>
          ))}
        </RhfSelect>
      </FlexBox>
    </div>
  );
}

function RhfTimeField<T extends FieldValues>(
  props: UseControllerProps<T>
): ReactElement {
  const { control, name } = props;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value, ref }, fieldState }) => (
        <TextField
          onChange={e => {
            const v = e.target.value;
            onChange(timeFromHHMM(v));
          }}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          onBlur={onBlur}
          value={timeToHHMM(value)}
          type="time"
          sx={inlineFormControlStyle}
          ref={ref}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error && fieldState.error.message}
        />
      )}
    />
  );
}
